import type {
  PublicPracticeArtist,
  PublicPracticeCatalog,
  PublicPracticeSongCard,
} from './types';

export const PUBLIC_PRACTICE_CATALOG_BOOTSTRAP_ID =
  'public-practice-catalog-bootstrap';
export const PUBLIC_PRACTICE_ARTIST_BOOTSTRAP_ID =
  'public-practice-artist-bootstrap';
export const PUBLIC_PRACTICE_INDEX_BOOTSTRAP_SCHEMA_VERSION = 1;

const CARD_KEYS = [
  'song_key',
  'slug',
  'title',
  'artist_name',
  'artist_slug',
  'release_year',
  'genre',
  'difficulty',
  'vocal_range',
  'version_label',
  'hero_summary',
  'hub_path',
  'published_at',
] as const;

const CATALOG_KEYS = ['songs'] as const;
const ARTIST_KEYS = ['slug', 'name', 'schema_type', 'songs'] as const;
const SAFE_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const SAFE_SONG_KEY = /^[a-z0-9][a-z0-9-]{0,63}$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DIFFICULTIES = new Set(['beginner', 'intermediate', 'advanced']);

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasExactKeys(
  value: Record<string, unknown>,
  keys: readonly string[],
): boolean {
  const actual = Object.keys(value);
  return (
    actual.length === keys.length
    && actual.every((key) => keys.includes(key))
  );
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isSafeSlug(value: unknown): value is string {
  return isNonEmptyString(value) && SAFE_SLUG.test(value);
}

export function parsePublicPracticeSongCardValue(
  value: unknown,
): PublicPracticeSongCard | null {
  if (!isObject(value) || !hasExactKeys(value, CARD_KEYS)) {
    return null;
  }

  const valid = (
    isNonEmptyString(value.song_key)
    && SAFE_SONG_KEY.test(value.song_key)
    && isSafeSlug(value.slug)
    && isNonEmptyString(value.title)
    && isNonEmptyString(value.artist_name)
    && isSafeSlug(value.artist_slug)
    && typeof value.release_year === 'number'
    && Number.isInteger(value.release_year)
    && value.release_year >= 1800
    && value.release_year <= 2100
    && isNonEmptyString(value.genre)
    && isNonEmptyString(value.difficulty)
    && DIFFICULTIES.has(value.difficulty)
    && isNonEmptyString(value.vocal_range)
    && isNonEmptyString(value.version_label)
    && isNonEmptyString(value.hero_summary)
    && value.hub_path === `/ko/practice-songs/${value.slug}`
    && isNonEmptyString(value.published_at)
    && ISO_DATE.test(value.published_at)
  );

  return valid ? value as unknown as PublicPracticeSongCard : null;
}

export function parsePublicPracticeCatalogValue(
  value: unknown,
): PublicPracticeCatalog | null {
  if (
    !isObject(value)
    || !hasExactKeys(value, CATALOG_KEYS)
    || !Array.isArray(value.songs)
  ) {
    return null;
  }
  const songs = value.songs.map(parsePublicPracticeSongCardValue);
  if (songs.some((song) => song === null)) {
    return null;
  }
  const typedSongs = songs as PublicPracticeSongCard[];
  if (new Set(typedSongs.map((song) => song.song_key)).size !== typedSongs.length) {
    return null;
  }
  if (new Set(typedSongs.map((song) => song.slug)).size !== typedSongs.length) {
    return null;
  }
  return { songs: typedSongs };
}

export function parsePublicPracticeArtistValue(
  value: unknown,
): PublicPracticeArtist | null {
  if (
    !isObject(value)
    || !hasExactKeys(value, ARTIST_KEYS)
    || !isSafeSlug(value.slug)
    || !isNonEmptyString(value.name)
    || (value.schema_type !== 'Person' && value.schema_type !== 'MusicGroup')
    || !Array.isArray(value.songs)
    || value.songs.length === 0
  ) {
    return null;
  }
  const songs = value.songs.map(parsePublicPracticeSongCardValue);
  if (songs.some((song) => (
    song === null
    || song.artist_slug !== value.slug
    || song.artist_name !== value.name
  ))) {
    return null;
  }
  const typedSongs = songs as PublicPracticeSongCard[];
  if (new Set(typedSongs.map((song) => song.song_key)).size !== typedSongs.length) {
    return null;
  }
  return {
    slug: value.slug,
    name: value.name,
    schema_type: value.schema_type,
    songs: typedSongs,
  };
}

function readBootstrap(
  id: string,
  payloadKey: 'catalog' | 'artist',
): unknown | null {
  const script = document.getElementById(id);
  if (
    !(script instanceof HTMLScriptElement)
    || script.type !== 'application/json'
    || script.dataset.schemaVersion
      !== String(PUBLIC_PRACTICE_INDEX_BOOTSTRAP_SCHEMA_VERSION)
  ) {
    return null;
  }
  try {
    const payload: unknown = JSON.parse(script.textContent ?? '');
    if (
      !isObject(payload)
      || !hasExactKeys(payload, ['schemaVersion', payloadKey])
      || payload.schemaVersion !== PUBLIC_PRACTICE_INDEX_BOOTSTRAP_SCHEMA_VERSION
    ) {
      return null;
    }
    return payload[payloadKey];
  } catch {
    return null;
  }
}

export function parsePublicPracticeCatalogBootstrap(
  pathname: string,
): PublicPracticeCatalog | null {
  if (pathname !== '/ko/practice-songs') {
    return null;
  }
  return parsePublicPracticeCatalogValue(
    readBootstrap(PUBLIC_PRACTICE_CATALOG_BOOTSTRAP_ID, 'catalog'),
  );
}

export function parsePublicPracticeArtistBootstrap(route: {
  locale: string;
  artistSlug: string;
  pathname: string;
}): PublicPracticeArtist | null {
  if (
    route.locale !== 'ko'
    || route.pathname !== `/ko/artists/${route.artistSlug}`
  ) {
    return null;
  }
  const artist = parsePublicPracticeArtistValue(
    readBootstrap(PUBLIC_PRACTICE_ARTIST_BOOTSTRAP_ID, 'artist'),
  );
  return artist?.slug === route.artistSlug ? artist : null;
}
